import { useState, useEffect, useMemo, useCallback } from 'react';
import './tasks-ehr-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

/* ── EhrInfoTip — ⓘ tooltip next to page title ── */
const EhrInfoTip = () => (
  <HeaderInfoTip
    spaced
    maxWidth={320}
    title="About EHR Tasks"
    body="This page displays pending resident changes received from the integrated EHR system. Each task must be reviewed and approved or rejected before it takes effect in PalCare."
    sections={[
      { heading: 'Move-in Resident', body: 'New residents scheduled to move in. Some may be pre-scheduled by the EHR and cannot be approved until the resident is officially allocated their area on the move-in date.' },
      { heading: 'Relocate Resident', body: 'Residents being moved from one area to another within the campus.' },
      { heading: 'Move-out Resident', body: 'Residents that have been discharged or moved out in the EHR and need to be marked inactive in PalCare.' },
      { heading: 'Resident info updates', body: 'Changes to resident details (name, phone, email, care level, etc.) from the EHR are approved automatically and listed in History.' },
    ]}
  />
);

import EhrTaskTable, {
  ADD_COLUMN_DEFS,
  MOVE_COLUMN_DEFS,
  REMOVE_COLUMN_DEFS,
} from './components/EhrTaskTable.jsx';
import ApproveAddDialog from './components/ApproveAddDialog.jsx';
import ApproveAddUserDialog from './components/ApproveAddUserDialog.jsx';
import ConfirmActionDialog from './components/ConfirmActionDialog.jsx';
import EhrHistoryView from './components/EhrHistoryView.jsx';
// Shared components
import ActiveFilterBar from '../../../components/v2/ActiveFilterBar.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import TableSkeleton from '../../../components/v2/TableSkeleton.jsx';
// V2 data layer
import {
  getSession,
  getCampusId,
  getUserId,
  getSessionId,
  getCampusList,
  updateSessionCampus,
  getUsername,
  getEhrInfo,
  setEhrStgCount,
  getCampusTzOffset,
} from '../../../services/session.js';
import {
  fetchStagedTasks,
  fetchResidentGroups,
  approveAddTask,
  approveMoveTask,
  approveRemoveTask,
  rejectTask,
  fetchStagedUsers,
  rejectUserTask,
} from './api/tasksEhrApi.js';
import {
  fetchUserGroupList,
  fetchRoles,
  addUser,
  deleteUsers,
} from '../../users/api/usersApi.js';
import { convertIsoToLocalDate } from '../../../libs.jsx';
import HeaderInfoTip from '../../../components/v2/HeaderInfoTip.jsx';

// ---------------------------------------------------------------------------
// Tab configuration
// ---------------------------------------------------------------------------
const RESIDENT_TABS = [
  { key: 'add',    label: 'Move-in Resident',     emptyMsg: 'No move-in tasks to approve' },
  { key: 'move',   label: 'Relocate Resident',    emptyMsg: 'No relocating tasks to approve' },
  { key: 'remove', label: 'Move-out Resident',    emptyMsg: 'No move-out tasks to approve' },
  /* No Update Resident Info tab (removed 4.3.11): EHR resident info updates are approved
     automatically, and show in History as "Auto Approved". */
];

/* reject_reason is VARCHAR(500) on ext_resident_staged / ext_stg_user. The Lambda also
   trims and caps it, so this limit is a convenience, not the guard. */
const REJECT_REASON_MAX = 500;

const USER_TABS = [
  { key: 'user-add',    label: 'Add User',    emptyMsg: 'No add user tasks to approve' },
  { key: 'user-remove', label: 'Remove User', emptyMsg: 'No remove user tasks to approve' },
];

const ALL_TABS = [...RESIDENT_TABS, ...USER_TABS];
const USER_TAB_KEYS = USER_TABS.map((t) => t.key);

/** Created On — auto-generated MySQL UTC timestamp, rendered campus-local. */
const CREATED_ON_COLUMN = { key: 'create_time', label: 'Created On', sortable: true, filterable: false, type: 'datetime-utc' };

/** User staged columns */
const USER_ADD_COLUMN_DEFS = [
  { key: 'display_name',  label: 'Display Name', sortable: true, filterable: true },
  { key: 'email',         label: 'Email',        sortable: true, filterable: true },
  { key: 'phone',         label: 'Phone',        sortable: true, filterable: true },
  { key: 'ehr_job_title', label: 'Title',        sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

const USER_REMOVE_COLUMN_DEFS = [
  { key: 'display_name', label: 'Display Name', sortable: true, filterable: true },
  { key: 'email',        label: 'Email',        sortable: true, filterable: true },
  { key: 'username',     label: 'Username',     sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

/** Map tab key -> column definitions */
const TAB_COLUMNS = {
  add: ADD_COLUMN_DEFS,
  move: MOVE_COLUMN_DEFS,
  remove: REMOVE_COLUMN_DEFS,
  'user-add':    USER_ADD_COLUMN_DEFS,
  'user-remove': USER_REMOVE_COLUMN_DEFS,
};

/** Map tab key -> search keys */
const TAB_SEARCH_KEYS = {
  add:    ['resident_first_name', 'resident_last_name', 'resident_nickname', 'resident_phone', 'resident_email', 'area_name'],
  move:   ['resident_name', 'old_area_name', 'area_name'],
  remove: ['resident_name', 'area_name'],
  'user-add':    ['display_name', 'email', 'phone', 'ehr_job_title', 'username'],
  'user-remove': ['display_name', 'email', 'username'],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TasksEhrPage -- V2 EHR staged-task approval page.
 *
 * Displays tabs of pending EHR tasks (Move-in, Relocate, Move-out, and
 * Add / Remove User) with search, column filters, sorting, and pagination.
 * Users click a row's Review button (or the row) to open an approval dialog and
 * approve or reject.
 */
const TasksEhrPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const session = getSession();
    if (!session) return '';
    const cfg = session.campusData?.[campusId.toString()];
    return cfg?.name || session.campus || '';
  }, [campusId]);
  // Campus UTC offset for rendering UTC timestamps (e.g. create_time) as local.
  const tzOffset = useMemo(() => getCampusTzOffset(campusId), [campusId]);

  /** Session params for API write calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  /** Campus change: check ehr_active, redirect to / if inactive */
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    if (!getEhrInfo(val).active) {
      updateSessionCampus(val);
      window.location.href = '/';
      return;
    }
    setCampusId(val);
    updateSessionCampus(val);
  }, []);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const { toast, showToast, dismissToast } = useToast(6000);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [addData, setAddData] = useState([]);
  const [moveData, setMoveData] = useState([]);
  const [removeData, setRemoveData] = useState([]);
  const [groups, setGroups] = useState([]);
  // Staged user tasks
  const [userAddData, setUserAddData] = useState([]);
  const [userRemoveData, setUserRemoveData] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Parent-tab gating — driven by the EHR vendor's receiving flags
  // (is_receiving → resident data, is_receiving_user → user data).
  const ehrInfo = getEhrInfo(campusId);
  const showResidentsTab = !!ehrInfo.receiving_resident;
  const showUsersTab = !!ehrInfo.receiving_user;

  // Shared controls
  const [activeTab, setActiveTab] = useState(showResidentsTab ? 'add' : 'user-add');
  const parentTab = USER_TAB_KEYS.includes(activeTab) ? 'users' : 'residents';
  /* 'pending' (the Residents / Users tabs) or 'history' (one combined list, plan §8).
     Kept apart from activeTab so the pending tabs' state is untouched while History shows. */
  const [view, setView] = useState('pending');
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = usePersistedPerPage();

  // Per-tab filter / sort / pagination
  const [tabState, setTabState] = useState({
    add:           { filters: {}, sortColumn: 'resident_first_name', sortOrder: 'asc', page: 1 },
    move:          { filters: {}, sortColumn: 'resident_name',       sortOrder: 'asc', page: 1 },
    remove:        { filters: {}, sortColumn: 'resident_name',       sortOrder: 'asc', page: 1 },
    'user-add':    { filters: {}, sortColumn: 'display_name',        sortOrder: 'asc', page: 1 },
    'user-remove': { filters: {}, sortColumn: 'display_name',        sortOrder: 'asc', page: 1 },
  });

  /** Helper: update one key in the active tab's state slice */
  const updateTabState = useCallback((key, value) => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [key]: value },
    }));
  }, [activeTab]);

  // Dialog state
  const [activeDialog, setActiveDialog] = useState(null);     // 'add'|'move'|'remove'|'user-add'|'user-remove'|'reject' | null
  const [selectedTask, setSelectedTask] = useState(null);      // row object
  const [previousDialog, setPreviousDialog] = useState(null);  // for reject->return flow
  // Required, free text (docs/EHR_TASK_HISTORY_PLAN.md decision 11). Saved on the staged row
  // so History can show why a task was rejected.
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const tasks = [];
      if (showResidentsTab) {
        tasks.push(fetchStagedTasks(campusId));
        tasks.push(fetchResidentGroups(campusId));
      } else {
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
      }
      if (showUsersTab) {
        tasks.push(fetchStagedUsers(campusId));
        tasks.push(fetchUserGroupList(campusId));
        tasks.push(fetchRoles());
      } else {
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
      }

      const [staged, grps, stagedUsers, uGroups, rolesData] = await Promise.all(tasks);

      /* staged[1] holds update-info tasks. They are approved automatically now, so they are
         neither shown nor counted: a count with no tab to act on it would never clear. */
      const addList    = staged?.length > 0 ? staged[0] : [];
      const moveList   = staged?.length > 2 ? staged[2] : [];
      const removeList = staged?.length > 3 ? staged[3] : [];
      setAddData(addList);
      setMoveData(moveList);
      setRemoveData(removeList);
      setGroups(grps || []);

      const userAddList    = stagedUsers?.length > 0 ? stagedUsers[0] : [];
      const userRemoveList = stagedUsers?.length > 1 ? stagedUsers[1] : [];
      setUserAddData(userAddList);
      setUserRemoveData(userRemoveList);
      setUserGroups(uGroups || []);
      setRoles(rolesData || []);

      // Update session EHR count (combined residents + users) on the EHR vendor
      // so the nav badge stays in sync without a full reload.
      const total =
        addList.length + moveList.length + removeList.length +
        userAddList.length + userRemoveList.length;
      setEhrStgCount(campusId, total);
    } catch (err) {
      console.error('[TasksEhrPage] Load error:', err);
      showToast('error', 'Failed to load EHR tasks');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast, showResidentsTab, showUsersTab]);

  // Initial load
  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 600 seconds (10 minutes)
  useEffect(() => {
    const interval = setInterval(() => { loadData(); }, 600000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ==========================================================================
  // RAW DATA LOOKUP
  // ==========================================================================
  const rawDataForTab = useMemo(() => ({
    add: addData,
    move: moveData,
    remove: removeData,
    'user-add': userAddData,
    'user-remove': userRemoveData,
  }), [addData, moveData, removeData, userAddData, userRemoveData]);

  /** Combined counts for parent tabs */
  const residentsTotal = addData.length + moveData.length + removeData.length;
  const usersTotal = userAddData.length + userRemoveData.length;

  // ==========================================================================
  // FILTER / SORT / PAGINATE — active tab only
  // ==========================================================================
  const currentTab = tabState[activeTab];

  const filtered = useMemo(() => {
    let result = [...(rawDataForTab[activeTab] || [])];
    const searchKeys = TAB_SEARCH_KEYS[activeTab];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((row) =>
        searchKeys.some((key) => {
          const val = row[key];
          return val != null && String(val).toLowerCase().includes(s);
        })
      );
    }

    // 2. Column filters
    Object.entries(currentTab.filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        const cellVal = row[columnKey];
        return values.includes(cellVal != null && cellVal !== '' ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[currentTab.sortColumn];
      let bVal = b[currentTab.sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return currentTab.sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [rawDataForTab, activeTab, search, currentTab.filters, currentTab.sortColumn, currentTab.sortOrder]);

  // Pagination
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(currentTab.page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  // Reset page on search/filter change
  useEffect(() => { updateTabState('page', 1); }, [search, currentTab.filters, perPage]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    TAB_COLUMNS[activeTab].forEach((c) => { map[c.key] = c.label; });
    return map;
  }, [activeTab]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setTabState((prev) => {
      const ts = prev[activeTab];
      if (ts.sortColumn === key) {
        return {
          ...prev,
          [activeTab]: { ...ts, sortOrder: ts.sortOrder === 'asc' ? 'desc' : 'asc' },
        };
      }
      return {
        ...prev,
        [activeTab]: { ...ts, sortColumn: key, sortOrder: 'asc' },
      };
    });
  }, [activeTab]);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        filters: { ...prev[activeTab].filters, [columnKey]: selectedValues },
      },
    }));
  }, [activeTab]);

  const handleRemoveFilter = useCallback((columnKey) => {
    setTabState((prev) => {
      const next = { ...prev[activeTab].filters };
      delete next[columnKey];
      return { ...prev, [activeTab]: { ...prev[activeTab], filters: next } };
    });
  }, [activeTab]);

  const handleClearAllFilters = useCallback(() => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], filters: {} },
    }));
  }, [activeTab]);

  // Row click -> open dialog (blocked for status 15 pending move-ins)
  const handleRowClick = useCallback((row) => {
    if (row.status === 15) return;
    setSelectedTask(row);
    setActiveDialog(activeTab);
    setPreviousDialog(null);
  }, [activeTab]);

  /** Switch parent tab → reset active subtab to first one in that group */
  const handleParentTabChange = useCallback((p) => {
    setView('pending');
    setActiveTab(p === 'residents' ? 'add' : 'user-add');
    setSearch('');
  }, []);

  // -- APPROVE handlers --

  const handleApproveAdd = useCallback(async (task, groupIds) => {
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: task.cloud_id,
        campus_id: campusId,
        resident_first_name: task.resident_first_name,
        resident_last_name: task.resident_last_name,
        resident_phone: task.resident_phone,
        resident_email: task.resident_email,
        resident_nickname: task.resident_nickname,
        ehr_level_of_care: task.ehr_level_of_care,
        is_area_assigned: true,
        area_id: task.area_id,
        resident_group_ids: groupIds,
        move_in_date: task.physical_move_in_date,
        ehr_resident_id: task.ehr_resident_id,
        ehr_campus_id: task.ehr_campus_id,
        modified_by: session.userId,
        vendor_id: getEhrInfo(campusId).vendor_id,
      };
      const result = await approveAddTask(payload, session);
      if (result && result[0] === true) {
        showToast('success', `${task.resident_first_name} ${task.resident_last_name} has been added`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during add: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve add error:', err);
      showToast('error', 'Failed to approve add task');
    } finally {
      setSubmitting(false);
    }
  }, [campusId, getSessionParams, showToast, loadData]);

  const handleApproveMove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: selectedTask.cloud_id,
        resident_id: selectedTask.tmp_resident_id,
        area_id: selectedTask.area_id,
        modified_by: session.userId,
      };
      const oldPayload = {
        resident_id: selectedTask.tmp_resident_id,
        area_id: selectedTask.old_area_id,
      };
      const result = await approveMoveTask(payload, oldPayload, session);
      if (result && result[0]) {
        showToast('success', `Resident moved successfully to ${selectedTask.area_name}`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during move: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve move error:', err);
      showToast('error', 'Failed to approve move task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  const handleApproveRemove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: selectedTask.cloud_id,
        resident_id: selectedTask.tmp_resident_id,
        move_out_date: selectedTask.physical_move_out_date,
        modified_by: session.userId,
      };
      const result = await approveRemoveTask(payload, session);
      if (result && result[0]) {
        showToast('success', `${selectedTask.resident_name} has been marked in-active`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during remove: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve remove error:', err);
      showToast('error', 'Failed to approve remove task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  // -- REJECT handlers --

  /** Open the reject confirmation dialog (from within any approve dialog) */
  const handleOpenReject = useCallback((task) => {
    setPreviousDialog(activeDialog);
    setRejectReason('');
    setActiveDialog('reject');
  }, [activeDialog]);

  /** Confirm rejection — dispatches to resident or user reject based on previousDialog */
  const handleConfirmReject = useCallback(async () => {
    if (!selectedTask) return;
    // Whitespace-only counts as empty; the Lambda refuses an empty reason too.
    const reason = rejectReason.trim();
    if (!reason) return;
    const isUser = previousDialog === 'user-add' || previousDialog === 'user-remove';
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const result = isUser
        ? await rejectUserTask(selectedTask.cloud_id, session, reason)
        : await rejectTask(selectedTask.cloud_id, session, reason);
      if (result && result[0]) {
        const typeLabel =
          previousDialog === 'add' ? 'Move-in'
          : previousDialog === 'move' ? 'Relocate'
          : previousDialog === 'remove' ? 'Move-out'
          : previousDialog === 'user-add' ? 'Add user'
          : 'Remove user';
        const name = isUser
          ? (selectedTask.display_name || selectedTask.username || 'task')
          : (selectedTask.resident_name || `${selectedTask.resident_first_name} ${selectedTask.resident_last_name}`);
        showToast('success', `${typeLabel} task for ${name} has been removed`);
        setActiveDialog(null);
        setSelectedTask(null);
        setPreviousDialog(null);
        setRejectReason('');
        loadData();
      } else {
        showToast('error', `Error during rejection: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Reject error:', err);
      showToast('error', 'Failed to reject task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, previousDialog, rejectReason, getSessionParams, showToast, loadData]);

  /** Cancel rejection -> return to previous dialog */
  const handleCancelReject = useCallback(() => {
    setActiveDialog(previousDialog);
    setPreviousDialog(null);
    setRejectReason('');
  }, [previousDialog]);

  // -- USER approval handlers --

  /** Approve a staged "Add User" task (action: new-task on usersApi) */
  const handleApproveUserAdd = useCallback(async (formData) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const result = await addUser(formData, session, true);
      if (result && result[0] === true) {
        showToast('success', `${formData.display_name} has been added`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during user add: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve user add error:', err);
      showToast('error', 'Failed to approve add user task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  /** Approve a staged "Remove User" task (action: remove-task on usersApi) */
  const handleApproveUserRemove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      /* user_id, cognito_id, sip_extension and (for this task type) username are the linked
         PalCare account's, from sp_getExtUserStgTasks v2. v1 returned none of them, so this
         sent undefined and the Lambda rejected the removal (docs/sp_getExtUserStgTasks_v2.sql). */
      const result = await deleteUsers([{
        user_id: selectedTask.user_id,
        username: selectedTask.username,
        cognito_id: selectedTask.cognito_id,
        sip_extension: selectedTask.sip_extension,
        cloud_id: selectedTask.cloud_id,
      }], { ...session, username: getUsername() }, true);
      if (result && result[0] === true) {
        showToast('success', `${selectedTask.display_name || selectedTask.username} has been removed`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during user remove: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve user remove error:', err);
      showToast('error', 'Failed to approve remove user task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);


  /** Close any dialog */
  const handleCloseDialog = useCallback(() => {
    setActiveDialog(null);
    setSelectedTask(null);
    setPreviousDialog(null);
  }, []);

  // ==========================================================================
  // RENDER HELPERS
  // ==========================================================================

  /** Render the dialog body for move confirmation */
  const renderMoveBody = () => {
    if (!selectedTask) return null;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to move <strong>{selectedTask.resident_name}</strong> from{' '}
        <strong>{selectedTask.old_area_name}</strong> to <strong>{selectedTask.area_name}</strong>?
      </p>
    );
  };

  /** Render the dialog body for remove confirmation */
  const renderRemoveBody = () => {
    if (!selectedTask) return null;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to confirm moving out <strong>{selectedTask.resident_name}</strong> from{' '}
        <strong>{selectedTask.area_name || selectedTask.old_area_name}</strong>?
      </p>
    );
  };

  /** Render the dialog body for reject confirmation */
  const renderRejectBody = () => {
    const vendorName = getEhrInfo(campusId).vendor_name || 'the EHR system';
    return (
      <>
        <p className="ehr-dialog__confirm-text">
          Are you sure you wish to reject this task? New changes within <strong>{vendorName}</strong> will
          be necessary to recreate the task.
        </p>
        <div className="dp-drawer__field">
          <label className="dp-drawer__label" htmlFor="ehr-reject-reason">Reason for rejecting (required)</label>
          <textarea
            id="ehr-reject-reason"
            className="dp-drawer__textarea"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            maxLength={REJECT_REASON_MAX}
            rows={3}
            placeholder="Why is this task being rejected?"
            aria-required="true"
            disabled={submitting}
            autoFocus
          />
          <span className="ehr-reject-reason__count" aria-live="polite">
            {rejectReason.length}/{REJECT_REASON_MAX}
          </span>
        </div>
      </>
    );
  };

  /** Render the dialog body for user-remove confirmation */
  const renderUserRemoveBody = () => {
    if (!selectedTask) return null;
    const name = selectedTask.display_name || selectedTask.username;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to remove user <strong>{name}</strong>?
      </p>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="ps-page">
      {/* Toast */}
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="ps-page-header">
        <div className="ps-page-header__inner">
          <div className="ps-breadcrumb-row">
            <div className="ps-breadcrumb">
              <span className="ps-breadcrumb__text">Tasks</span>
              <svg className="dp-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="ps-breadcrumb__current">EHR</span>
            </div>
            <div className="ps-campus-row">
              <label htmlFor="dp-ehr-campus-selector" className="ps-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="dp-ehr-campus-selector"
                  value={campusId}
                  onChange={handleCampusChange}
                  className="ps-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id} value={c.campus_id}>
                      {c.campus_name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ps-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="ps-page-title">EHR Tasks<EhrInfoTip /></h1>
        </div>
      </div>

      {/* ============================================================
          PARENT TAB BAR — Residents | Users
          Always rendered when at least one parent is enabled, with
          individual tabs gated by ehr_resident / ehr_user.
          ============================================================ */}
      {showResidentsTab || showUsersTab ? (
        <div className="ehr-tab-bar" role="tablist">
          <div className="ehr-tab-bar__inner">
            {showResidentsTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={view === 'pending' && parentTab === 'residents'}
                className={`ehr-tab ${view === 'pending' && parentTab === 'residents' ? 'is-active' : ''}`}
                onClick={() => handleParentTabChange('residents')}
              >
                Residents
                {residentsTotal > 0 ? (
                  <span className="ehr-tab__badge">{residentsTotal}</span>
                ) : null}
              </button>
            ) : null}
            {showUsersTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={view === 'pending' && parentTab === 'users'}
                className={`ehr-tab ${view === 'pending' && parentTab === 'users' ? 'is-active' : ''}`}
                onClick={() => handleParentTabChange('users')}
              >
                Users
                {usersTotal > 0 ? (
                  <span className="ehr-tab__badge">{usersTotal}</span>
                ) : null}
              </button>
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={view === 'history'}
              className={`ehr-tab ${view === 'history' ? 'is-active' : ''}`}
              onClick={() => setView('history')}
            >
              Task History
            </button>
          </div>
        </div>
      ) : null}

      {view === 'history' ? (
        <EhrHistoryView campusId={campusId} tzOffset={tzOffset} />
      ) : (
      <>
      {/* ============================================================
          SUBTAB BAR — segmented control for resident or user subtabs
          ============================================================ */}
      <div className="ehr-subtab-row">
        <div className="pp-subtab-toggle" role="tablist">
          {(parentTab === 'residents' ? RESIDENT_TABS : USER_TABS).map((tab) => {
            const count = (rawDataForTab[tab.key] || []).length;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                /* Guided-help anchor, one per subtab key: ehr.subtab-add /
                   -move / -remove. The lifecycle guides spotlight these
                   and detect arrival via `.is-active`. */
                data-help-id={`ehr.subtab-${tab.key}`}
                className={`pp-subtab-btn${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {count > 0 ? (
                  <span className="ehr-subtab-badge">{count}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        {/* Controls */}
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search tasks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search EHR tasks"
              />
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        <ActiveFilterBar
          filters={currentTab.filters}
          columnLabels={columnLabels}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* Table */}
        {loading ? (
          <TableSkeleton />
        ) : paginated.length === 0 ? (
          <div className="ehr-empty">
            <p className="ehr-empty__text">
              {ALL_TABS.find((t) => t.key === activeTab)?.emptyMsg || 'No tasks found'}
            </p>
          </div>
        ) : (
          <EhrTaskTable
            columnDefs={TAB_COLUMNS[activeTab]}
            data={paginated}
            allData={rawDataForTab[activeTab]}
            sortColumn={currentTab.sortColumn}
            sortOrder={currentTab.sortOrder}
            onSort={handleSort}
            filters={currentTab.filters}
            onFilterChange={handleFilterChange}
            onRowClick={handleRowClick}
            tzOffset={tzOffset}
          />
        )}

        {/* Pagination */}
        {!loading && totalFiltered > 0 ? (
          <Pagination
            page={safePage}
            totalPages={totalPages}
            total={totalFiltered}
            perPage={perPage}
            onPageChange={(n) => updateTabState('page', n)}
            onPerPageChange={setPerPage}
            entityLabel="tasks"
            selectId="dp-ehr-per-page"
          />
        ) : null}
      </div>
      </>
      )}

      {/* ============================================================
          DIALOGS
          ============================================================ */}

      {/* Add (Move-in) approval dialog */}
      {activeDialog === 'add' ? (
        <ApproveAddDialog
          task={selectedTask}
          groups={groups}
          onApprove={handleApproveAdd}
          onReject={handleOpenReject}
          onClose={handleCloseDialog}
          submitting={submitting}
          helpIdApprove="ehr.approve-add"
        />
      ) : null}

      {/* Move (Relocate) confirmation dialog */}
      {activeDialog === 'move' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Resident Relocate"
          onConfirm={handleApproveMove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
          helpIdApprove="ehr.approve-move"
        >
          {renderMoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* Remove (Move-out) confirmation dialog */}
      {activeDialog === 'remove' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Resident Move-out"
          onConfirm={handleApproveRemove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
          helpIdApprove="ehr.approve-remove"
        >
          {renderRemoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* User Add approval dialog */}
      {activeDialog === 'user-add' ? (
        <ApproveAddUserDialog
          task={selectedTask}
          roles={roles}
          campusId={campusId}
          userGroups={userGroups}
          onApprove={handleApproveUserAdd}
          onReject={() => handleOpenReject(selectedTask)}
          onClose={handleCloseDialog}
          submitting={submitting}
        />
      ) : null}

      {/* User Remove confirmation dialog */}
      {activeDialog === 'user-remove' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Remove User"
          onConfirm={handleApproveUserRemove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
        >
          {renderUserRemoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* Reject confirmation dialog */}
      {activeDialog === 'reject' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCancelReject}
          title="Confirm Task Rejection"
          onConfirm={handleConfirmReject}
          confirmLabel="Yes"
          submitting={submitting}
          confirmDisabled={!rejectReason.trim()}
        >
          {renderRejectBody()}
        </ConfirmActionDialog>
      ) : null}

    </div>
    </div>
    </>
  );
};

export default TasksEhrPage;
